import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import { Button, Loading } from 'framework/ui/mui-builder';
import { Document, Page } from 'react-pdf';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const SCROLL_END_THRESHOLD_PX = 50;

const isScrolledToEnd = (el: HTMLElement) => {
  const { scrollTop, scrollHeight, clientHeight } = el;
  return scrollTop + clientHeight >= scrollHeight - SCROLL_END_THRESHOLD_PX;
};

interface BlobPreviewApprovalFooterProps {
  onNext: () => void;
  onCancel: () => void;
  sendButtonText?: string;
  approvalLabel?: string;
}

interface BlobPreviewModalProps {
  open: boolean;
  blobUrl: string | null;
  fileName: string;
  onClose: () => void;
  loading?: boolean;
  approvalFooter?: BlobPreviewApprovalFooterProps;
}

/**
 * Similar to DocumentPreviewModal but works with blob URLs directly.
 */
const BlobPreviewModal: React.FC<BlobPreviewModalProps> = ({
  open,
  blobUrl,
  fileName,
  onClose,
  loading = false,
  approvalFooter,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [scale, setScale] = useState(1.2);
  const hasCalledScrollToEndRef = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [approvalChecked, setApprovalChecked] = useState(false);

  const hasApprovalFooter = Boolean(approvalFooter);

  useEffect(() => {
    if (!open) return;
    hasCalledScrollToEndRef.current = false;
    setScrolledToEnd(false);
    setApprovalChecked(false);
  }, [open]);

  useEffect(() => {
    if (!open || !blobUrl || !hasApprovalFooter || numPages !== 1) return;
    const id = setTimeout(() => {
      if (hasCalledScrollToEndRef.current) return;
      const el = scrollContainerRef.current;
      if (el && isScrolledToEnd(el)) {
        hasCalledScrollToEndRef.current = true;
        setScrolledToEnd(true);
      }
    }, 200);
    return () => clearTimeout(id);
  }, [open, blobUrl, hasApprovalFooter, numPages]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (hasCalledScrollToEndRef.current) return;
    if (!isScrolledToEnd(e.currentTarget)) return;
    hasCalledScrollToEndRef.current = true;
    setScrolledToEnd(true);
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages: pages }: { numPages: number }) => {
    setNumPages(pages);
  }, []);

  const zoomIn = useCallback(() => {
    setScale(s => Math.min(s + 0.2, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setScale(s => Math.max(s - 0.2, 0.6));
  }, []);

  const handleDownload = useCallback(() => {
    if (!blobUrl) return;

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [blobUrl, fileName]);

  const handlePrint = useCallback(() => {
    if (!blobUrl) return;

    const printWindow = window.open(blobUrl);
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    }
  }, [blobUrl]);

  if (!open) return null;

  if (loading) {
    return (
      <Dialog open fullWidth maxWidth="md">
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Loading />
        </DialogContent>
      </Dialog>
    );
  }

  if (!blobUrl) return null;

  return (
    <Dialog open fullWidth maxWidth="md" scroll="paper" onClose={onClose}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography component="span" variant="h5" noWrap sx={{ maxWidth: '60%' }}>
          {fileName}
        </Typography>
        <Box display="flex" gap="10px">
          <IconButton onClick={zoomOut} title="Zoom out">
            <ZoomOutIcon />
          </IconButton>
          <IconButton onClick={zoomIn} title="Zoom in">
            <ZoomInIcon />
          </IconButton>
          <IconButton onClick={handleDownload} title="Download">
            <DownloadIcon />
          </IconButton>
          <IconButton onClick={handlePrint} title="Print">
            <PrintIcon />
          </IconButton>
          <IconButton onClick={onClose} title="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        ref={hasApprovalFooter ? scrollContainerRef : undefined}
        onScroll={hasApprovalFooter ? handleScroll : undefined}
        sx={{
          overflow: 'auto',
          backgroundColor: 'background.default',
          minHeight: '500px',
          padding: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: 'fit-content',
            minWidth: '100%',
            margin: '0 auto',
          }}
        >
          <Document file={blobUrl} onLoadSuccess={onDocumentLoadSuccess} loading={<Loading />}>
            {Array.from({ length: numPages }, (_, index) => (
              <Box
                key={`page_${index}`}
                mb={2}
                sx={{
                  boxShadow: theme => `0 2px 8px ${theme.palette.common.shadowMedium || theme.palette.divider}`,
                  backgroundColor: 'common.white',
                }}
              >
                <Page renderAnnotationLayer={false} renderTextLayer={false} pageNumber={index + 1} scale={scale} />
              </Box>
            ))}
          </Document>
        </Box>
      </DialogContent>
      {approvalFooter && (
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', px: 2, pb: 2, pt: 0 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={approvalChecked}
                onChange={(_, checked) => setApprovalChecked(checked)}
                disabled={!scrolledToEnd}
              />
            }
            label={approvalFooter.approvalLabel ?? 'I have reviewed the document and approve its content for sending'}
            sx={{ mb: 1, ml: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="outlined" onClick={approvalFooter.onCancel}>
              Cancel
            </Button>
            <Button variant="contained" disabled={!scrolledToEnd || !approvalChecked} onClick={approvalFooter.onNext}>
              {approvalFooter.sendButtonText ?? 'Next'}
            </Button>
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default BlobPreviewModal;
